import i18nFactory from '@yandex-int/i18n';

import { ValidationErrorData } from '../../typings/common';
import { ConstData } from '../../typings/constants';
import {
    GdCampaignCheckPositionInterval,
    GdCampaignEmailEvent,
    GdCampaignSmsEvent
} from '../../typings/graphql-grid';
import {
    CampaignNotification,
    NotificationEditorState,
    NotificationsEditorValidationData,
    PercentOfCurrentBalanceNotification
} from '../../typings/notificationEditor';
import { parseNumber } from '../FormatHelpers/FormatHelpers';

import * as keyset from './NotificationEditorHelper.i18n';

const i18n = i18nFactory(keyset);

export enum NotificationType {
    currentBalance = 'currentBalance',
    endCampaign = 'endCampaign',
    moderationResults = 'moderationResults',
    monitoringResults = 'monitoringResults',
    increaseBalance = 'increaseBalance',
    stopByReachDailyBudget = 'stopByReachDailyBudget', // оба уведомления
    percentOfCurrentBalance = 'percentOfCurrentBalance',
    changeTrafficValue = 'changeTrafficValue',
    campaignEvents = 'campaignEvents',
    xlsReady = 'xlsReady'
}

export const notificationTitleByType = {
    [NotificationType.currentBalance]: i18n('current-balance'),
    [NotificationType.endCampaign]: i18n('end-campaign'),
    [NotificationType.moderationResults]: i18n('moderation-results'),
    [NotificationType.monitoringResults]: i18n('monitoring-results'),
    [NotificationType.increaseBalance]: i18n('increase-balance'),
    [NotificationType.stopByReachDailyBudget]: i18n(
        'stop-by-reach-daily-budget'
    ),
    [NotificationType.percentOfCurrentBalance]: i18n(
        'percent-of-current-balance'
    ),
    [NotificationType.changeTrafficValue]: i18n('change-traffic-value'),
    [NotificationType.campaignEvents]: i18n('campaign-events'),
    [NotificationType.xlsReady]: i18n('xls-ready')
};

export function getDefaultNotificationsEditorState(): NotificationEditorState {
    return {
        email: '',
        phone: '',
        notifications: [],
        smsInterval: {
            startTime: 0,
            endTime: 0,
            inputStartTime: '',
            inputEndTime: ''
        },
        isOpen: false,
        isChangedPhone: false
    };
}

export const validateNotificationItem = (
    notificationItem: CampaignNotification,
    constants?: ConstData
): Record<string, ValidationErrorData[]> => {
    if (!constants) {
        throw new Error('отсутсвует объект констант');
    }

    switch (notificationItem.type) {
        case NotificationType.percentOfCurrentBalance:
            return validatePercentOfCurrentBalanceNotification(
                notificationItem as PercentOfCurrentBalanceNotification,
                constants
            );
        default:
            return {};
    }
};

export const validatePercentOfCurrentBalanceNotification = (
    notificationItem: PercentOfCurrentBalanceNotification,
    constants: ConstData
): Record<string, ValidationErrorData[]> => {
    const { percent, value } = notificationItem;
    const {
        campaignConstants: {
            minWarningBalanceInPercent,
            maxWarningBalanceInPercent
        }
    } = constants;

    if (!value) {
        return {};
    }

    if (percent === undefined || percent === '') {
        return {
            percent: [
                {
                    code: 'percent-of-current-balance-warning-empty',
                    text: i18n('percent-of-current-balance-warning-empty')
                }
            ]
        };
    }

    if (
        parseNumber(percent) > maxWarningBalanceInPercent ||
        parseNumber(percent) < minWarningBalanceInPercent
    ) {
        return {
            percent: [
                {
                    code: 'percent-of-current-balance-warning-interval',
                    text: i18n('percent-of-current-balance-warning-interval', {
                        min: minWarningBalanceInPercent,
                        max: maxWarningBalanceInPercent
                    })
                }
            ]
        };
    }

    return {};
};

export const validateCommonFieldsNotificationEditor = (
    editor: NotificationEditorState
): Record<string, ValidationErrorData[]> => {
    const { email } = editor;

    return {
        email: validateEmailNotificationEditor(email)
    };
};

export const validateEmailNotificationEditor = (
    email: string
): ValidationErrorData[] => {
    if (email.trim().length === 0) {
        return [
            {
                code: 'email-empty',
                text: i18n('email-empty')
            }
        ];
    }

    return [];
};

export const hasValidationErrors = (
    errorsData: Record<string, ValidationErrorData[]>
): boolean => {
    return Object.keys(errorsData).some(
        keyError => errorsData[keyError] && errorsData[keyError].length > 0
    );
};

export const validateNotificationsEditor = (
    editor: NotificationEditorState,
    constants?: ConstData
): NotificationsEditorValidationData => {
    const commonErrors = validateCommonFieldsNotificationEditor(editor);
    const hasCommonErrors = hasValidationErrors(commonErrors);
    let hasNotificationsErrors = false;
    const notificationsErrors = editor.notifications.map(notificationItem => {
        const itemErrors = validateNotificationItem(
            notificationItem,
            constants
        );
        const hasItemErrors = hasValidationErrors(itemErrors);
        if (hasItemErrors) {
            hasNotificationsErrors = true;
        }

        return itemErrors;
    });

    return {
        common: commonErrors,
        notifications: notificationsErrors,
        isValid: !hasCommonErrors && !hasNotificationsErrors
    };
};

export const mapSmsNotificationsType = {
    [GdCampaignSmsEvent.FINISHED]: NotificationType.endCampaign,
    [GdCampaignSmsEvent.MODERATION]: NotificationType.moderationResults,
    [GdCampaignSmsEvent.MONEY_IN]: NotificationType.increaseBalance,
    [GdCampaignSmsEvent.MONEY_OUT]: NotificationType.currentBalance,
    [GdCampaignSmsEvent.MONITORING]: NotificationType.monitoringResults,
    [GdCampaignSmsEvent.STOP_BY_REACH_DAILY_BUDGET]:
        NotificationType.stopByReachDailyBudget
};

export const mapSmsNotificationsApiType = {
    [NotificationType.endCampaign]: GdCampaignSmsEvent.FINISHED,
    [NotificationType.moderationResults]: GdCampaignSmsEvent.MODERATION,
    [NotificationType.increaseBalance]: GdCampaignSmsEvent.MONEY_IN,
    [NotificationType.currentBalance]: GdCampaignSmsEvent.MONEY_OUT,
    [NotificationType.monitoringResults]: GdCampaignSmsEvent.MONITORING,
    [NotificationType.stopByReachDailyBudget]:
        GdCampaignSmsEvent.STOP_BY_REACH_DAILY_BUDGET
};

export const mapEmailNotificationsType = {
    [GdCampaignEmailEvent.CHECK_POSITION]: NotificationType.changeTrafficValue,
    [GdCampaignEmailEvent.SEND_ACCOUNT_NEWS]: NotificationType.campaignEvents,
    [GdCampaignEmailEvent.STOP_BY_REACH_DAILY_BUDGET]:
        NotificationType.stopByReachDailyBudget,
    [GdCampaignEmailEvent.WARNING_BALANCE]:
        NotificationType.percentOfCurrentBalance,
    [GdCampaignEmailEvent.XLS_READY]: NotificationType.xlsReady
};
export const mapNotificationCheckPositionIntervalFromApiFormat = {
    [GdCampaignCheckPositionInterval.M_15]: '15',
    [GdCampaignCheckPositionInterval.M_30]: '30',
    [GdCampaignCheckPositionInterval.M_60]: '60'
};

export const mapNotificationCheckPositionIntervalToApiFormat = {
    '15': GdCampaignCheckPositionInterval.M_15,
    '30': GdCampaignCheckPositionInterval.M_30,
    '60': GdCampaignCheckPositionInterval.M_60
};

export const orderSmsNotifications = [
    NotificationType.currentBalance,
    NotificationType.endCampaign,
    NotificationType.moderationResults,
    NotificationType.monitoringResults,
    NotificationType.increaseBalance,
    NotificationType.stopByReachDailyBudget
];

export const orderEmailNotifications = [
    NotificationType.percentOfCurrentBalance,
    NotificationType.changeTrafficValue,
    NotificationType.stopByReachDailyBudget,
    NotificationType.campaignEvents,
    NotificationType.xlsReady
];

export const sortNotifications = (
    notifications: CampaignNotification[],
    orderNotifications: NotificationType[]
): CampaignNotification[] => {
    return notifications.sort((a, b) => {
        const indexA = orderNotifications.indexOf(a.type);
        const indexB = orderNotifications.indexOf(b.type);
        if (indexA === -1 || indexB === -1) {
            return 0;
        }

        return indexA - indexB;
    });
};
